import { TextInput } from "nhsuk-react-components"
import { useRef, useState, type ChangeEvent } from "react"
import "./DateTimeInput.scss"
interface DateInputValue {
  day: string
  month: string
  year: string
}
interface TimeInputValue {
  hour: string
  minute: string
}

export interface DateTimeInputValue {
  dateValue?: DateInputValue
  timeValue?: TimeInputValue
}
interface Props {
  autoSelectNext?: boolean
  hasDate: boolean
  hasTime: boolean
  dateTimeValue: DateTimeInputValue
  onChange: (dateTimeValue: DateTimeInputValue) => void
  error?: boolean
}

interface DateInputState {
  error?: boolean
  value: DateInputValue
}

interface TimeInputState {
  error?: boolean
  value: TimeInputValue
}

type FieldName = "day" | "month" | "year" | "hour" | "minute"

export function DateTimeInput({
  hasDate,
  hasTime,
  dateTimeValue,
  autoSelectNext,
  onChange,
  error,
}: Props): JSX.Element {
  const dayRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)
  const hourRef = useRef<HTMLInputElement>(null)
  const minuteRef = useRef<HTMLInputElement>(null)

  const [dateInputState, setDateInputState] = useState<DateInputState>({
    error: false,
    value: dateTimeValue.dateValue ?? { day: "", month: "", year: "" },
  })
  const [timeInputState, setTimeInputState] = useState<TimeInputState>({
    error: false,
    value: dateTimeValue.timeValue ?? { hour: "", minute: "" },
  })

  const handleSelectNext = (inputType: FieldName, value: string): void => {
    if (!(autoSelectNext ?? false)) return

    if (inputType === "day" && value.length === 2 && monthRef.current != null) {
      monthRef.current.focus()
    } else if (inputType === "month" && value.length === 2 && yearRef.current != null) {
      yearRef.current.focus()
    } else if (inputType === "year" && value.length === 4 && hourRef.current != null) {
      hourRef.current.focus()
    } else if (inputType === "hour" && value.length === 2 && minuteRef.current != null) {
      minuteRef.current.focus()
    }
  }

  const handleChange = (inputType: FieldName, event: ChangeEvent<HTMLInputElement>): void => {
    handleSelectNext(inputType, event.target.value)
    event.stopPropagation()

    switch (inputType) {
      case "day":
      case "month":
      case "year":
        updateDateValue()
        break
      case "hour":
      case "minute":
        updateTimeValue()
        break
    }
  }

  return (
    <>
      {hasDate && (
        <div className="date-time-input">
          <TextInput
            label="Day"
            inputMode="numeric"
            inputRef={dayRef}
            defaultValue={dateInputState.value.day}
            error={error}
            {...getPropsForField("day")}
          />
          <TextInput
            label="Month"
            inputMode="numeric"
            inputRef={monthRef}
            defaultValue={dateInputState.value.month}
            error={error}
            {...getPropsForField("month")}
          />
          <TextInput
            label="Year"
            inputMode="numeric"
            inputRef={yearRef}
            defaultValue={dateInputState.value.year}
            error={error}
            {...getPropsForField("year")}
          />
        </div>
      )}
      {hasTime && (
        <div className="date-time-input">
          <TextInput
            label="Hour"
            inputMode="numeric"
            inputRef={hourRef}
            defaultValue={timeInputState.value.hour}
            error={error}
            {...getPropsForField("hour")}
          />
          <TextInput
            label="Minute"
            inputMode="numeric"
            inputRef={minuteRef}
            defaultValue={timeInputState.value.minute}
            error={error}
            {...getPropsForField("minute")}
          />
        </div>
      )}
    </>
  )

  function getPropsForField(inputType: FieldName): object {
    switch (inputType) {
      case "day":
      case "month":
      case "year":
        return {
          pattern: "[0-9]*",
          className: inputType === "year" ? "nhsuk-input--width-4" : "nhsuk-input--width-2",
          onChange: (event: ChangeEvent<HTMLInputElement>) => {
            handleChange(inputType, event)
          },
          error: dateInputState.error,
          "aria-invalid": dateInputState.error,
        }
      case "hour":
      case "minute":
        return {
          pattern: "[0-9]*",
          className: "nhsuk-input--width-2",
          onChange: (event: ChangeEvent<HTMLInputElement>) => {
            handleChange(inputType, event)
          },
          error: timeInputState.error,
          "aria-invalid": dateInputState.error,
        }
    }
  }

  function updateDateValue(): void {
    const year = yearRef.current?.value ?? ""
    const month = monthRef.current?.value ?? ""
    const day = dayRef.current?.value ?? ""

    const isValidDate = new Date(`${year}-${month}-${day}`).getTime() > 0
    const dateValue = { day, month, year }
    setDateInputState({
      value: dateValue,
      error: !isValidDate,
    })

    if (isValidDate) {
      onChange({ ...dateTimeValue, dateValue })
    }
  }
  function updateTimeValue(): void {
    const hour = hourRef.current?.value ?? ""
    const minute = minuteRef.current?.value ?? ""
    const isValidHHMString = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(`${hour}:${minute}`)
    const timeValue = { hour, minute }

    setTimeInputState({
      value: timeValue,
      error: !isValidHHMString,
    })

    if (isValidHHMString) {
      onChange({ ...dateTimeValue, timeValue })
    }
  }
}
